/**
 * Test-report vocabulary that crosses the wire.
 *
 * The ledger itself stays in the backend (`operations/testReport/ledgerTypes.ts`)
 * — it is built there, stored whole, and nothing outside needs to reason about
 * how. What lives HERE is the small part a reader also has to name: the buckets
 * a lost mark can land in, and the cohort cut a teacher is shown.
 *
 * Both halves are one definition rather than two, because the alternative is a
 * frontend copy that drifts from the builder the first time a bucket is renamed
 * — a class of bug this repo has already paid for elsewhere.
 *
 * See docs/FeatureDesigns/student-performance-report-diagnosis/test-report-target.md.
 */

/**
 * Where a lost mark went. Every lost mark lands in exactly one, and the four
 * plus `attained` reconcile to the paper total. They are ordered by how fast the
 * underlying problem can be fixed, which is the point of separating them: a mark
 * sheet cannot tell them apart and they need opposite advice.
 */
export type LedgerBucket =
    | 'didntKnow'      // no workable method — practice, months
    | 'brokeAtSetup'   // knew the maths, took the question into it wrongly — weeks
    | 'brokeOnTheWay'  // sound set-up, slipped inside it — weeks
    | 'didntCommit';   // correct working, no answer given — one test

/**
 * How confidently a bucket was assigned. Carried so a report can say which
 * findings rest on a named cause and which on an inference — TR-17 honesty made
 * structural rather than promised.
 */
export type BucketBasis =
    | 'structural'   // from working/answer presence alone; no judgement involved
    | 'diagnosis'    // the analyser named the cause
    | 'depth';       // inferred from where the solution broke — weakest

/**
 * Why a question was never attempted. A run of blanks at the END of a paper is a
 * rationing failure — the student never got there — and prescribing revision for
 * it is answering the wrong question. The same blanks scattered through the
 * paper are a selection or knowledge signal.
 */
export type UnattemptedShape = 'ran-out' | 'scattered';

/** What a bucket is called where a person will read it. */
export const LEDGER_BUCKET_LABELS: Readonly<Record<LedgerBucket, string>> = {
    didntKnow: 'No workable method',
    brokeAtSetup: 'Broke at set-up',
    brokeOnTheWay: 'Slipped on the way',
    didntCommit: 'Worked, never answered',
};

// ── The cohort cut (target §8) ───────────────────────────────────────

/**
 * A (question, bucket, cause) triple that more than one student landed in.
 *
 * The triple is the unit because each part changes the teaching response. Same
 * question, same bucket, different causes is a hard question; same question,
 * same bucket, SAME cause is one thing taught once and misunderstood the same
 * way — which is the finding worth a teacher's time.
 */
export interface CohortCluster {
    questionId: string;
    /** Position on the paper, so the view can read in paper order. */
    number: number;
    bucket: LedgerBucket;
    /** The analyser's named category. Absent means the students share a bucket
     *  but no named cause — a weaker finding, deliberately still surfaced,
     *  because `diagnosisCategory` is present on roughly a third of attempts and
     *  requiring it would hide most of what happened. */
    cause?: string;
    /** Students in this cluster, and how many sat the question at all. */
    students: number;
    sat: number;
    /** students / sat. The denominator is who sat THIS question rather than the
     *  whole cohort, so a question only some students reached is not diluted by
     *  the ones who never saw it. */
    share: number;
    /** Marks the cluster cost between them. Zero on a paper with no marking
     *  scheme, where `students` is the whole finding. */
    marksLost: number;
}

/** What the cohort did with the paper, as distinct from where it broke. */
export interface CohortStrategy {
    students: number;
    /** Means over students, not over questions — a student who answered three
     *  questions counts once, like everyone else. */
    meanAccuracy: number;
    meanAttemptRate: number;
    /** Summed across the cohort. Worked and left unanswered is the cheapest
     *  finding on any paper and the only one that costs nothing to fix. */
    questionsLeftOnTable: number;
    marksLeftOnTable: number;
    /** Solved it, marked something else. Countable per cohort is precisely what
     *  TR-20 asks for. */
    transferSlips: number;
    /** The reading is disputed — the population a re-read would resolve. */
    contested: number;
    /** Students with a run of blanks at the END of the paper. Many of these is a
     *  fact about the paper's length, not about the students. */
    ranOutOfTime: number;
}

export interface CohortReport {
    questionSetId: string;
    /** Students the paper was given to, as far as this reader may see them —
     *  the denominator for `reportable`. Zero when the roster is unknown. */
    assignedStudents: number;
    /**
     * Is there enough of the class here for a class-level reading to mean
     * anything?
     *
     * A cohort cut over one or two reports is not a quiet version of the
     * finding — it is a different claim, made about a handful of students and
     * read as though it were about the class. So the card does not appear until
     * enough of the paper has come back: see `COHORT_MIN_REPORTS` and
     * `COHORT_MIN_COVERAGE` in the builder for the two numbers and why.
     *
     * Decided in the builder rather than the view because it is the same kind of
     * judgement as the cluster thresholds beside it, and because a client that
     * forgot to check it would publish exactly the reading this prevents.
     */
    reportable: boolean;
    strategy: CohortStrategy;
    /** Above threshold, worst first. */
    clusters: CohortCluster[];
    /** Clusters that existed but did not clear the threshold. Reported as a
     *  count so the view can say "and 12 smaller patterns" rather than implying
     *  these were everything. */
    belowThreshold: number;
}
